import { create } from 'zustand';
import { devtools, persist, createJSONStorage } from 'zustand/middleware';
import { registerSwrReset } from './registry';
import { LOAD_ERROR_MESSAGE, userFacingError } from '../../lib/userFacingErrors';

/**
 * Factory for a single-object SWR store backed by localStorage.
 * Use for resources that are one-per-user rather than lists/keyed (e.g. billing usage,
 * user profile, feature flags).
 *
 * @param {Object} cfg
 * @param {string} cfg.name — devtools store name
 * @param {string} cfg.storageKey — localStorage key
 * @param {() => Promise<Object>} cfg.fetcher — returns the data object
 */
export function createSwrSingletonStore({ name, storageKey, fetcher }) {
  const initialState = {
    data: null,
    hasFetched: false,
    cachedForUserId: null,
    isFetching: false,
    error: null,
  };

  const useStore = create(
    devtools(
      persist(
        (set) => ({
          ...initialState,

          setData: (data) => set({ data, hasFetched: true, error: null }),
          patchData: (updates) =>
            set((state) => ({
              data: state.data ? { ...state.data, ...updates } : updates,
              hasFetched: true,
            })),
          setCachedForUserId: (cachedForUserId) => set({ cachedForUserId }),
          setError: (error) => set({ error }),
          reset: () => set(() => initialState),
        }),
        {
          name: storageKey,
          version: 1,
          storage: createJSONStorage(() => localStorage),
          partialize: (state) => ({
            data: state.data,
            hasFetched: state.hasFetched,
            cachedForUserId: state.cachedForUserId,
          }),
        }
      ),
      { name }
    )
  );

  async function fetchIfStale({ userId = null, force = false } = {}) {
    const state = useStore.getState();

    if (userId && state.cachedForUserId && state.cachedForUserId !== userId) {
      state.reset();
    }

    if (state.isFetching && !force) return;

    useStore.setState({ isFetching: true });
    try {
      const data = await fetcher();
      const next = useStore.getState();
      next.setData(data);
      if (userId) next.setCachedForUserId(userId);
    } catch (err) {
      console.error(`[${name}] Failed to refresh data`, err);
      const existing = useStore.getState().data;
      if (!existing) useStore.setState({ error: userFacingError(err, LOAD_ERROR_MESSAGE) });
    } finally {
      useStore.setState({ isFetching: false });
    }
  }

  function useData(userId = null) {
    const data = useStore((s) => s.data);
    const hasFetched = useStore((s) => s.hasFetched);
    const error = useStore((s) => s.error);
    const cachedForUserId = useStore((s) => s.cachedForUserId);

    const userMatches = !userId || !cachedForUserId || cachedForUserId === userId;

    return {
      data: userMatches ? data : null,
      hasCache: hasFetched && userMatches,
      error,
    };
  }

  registerSwrReset(() => useStore.getState().reset());

  return { useStore, useData, fetchIfStale };
}
